import {
  confirmResetPassword,
  resetPassword,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { Field, Form, Formik } from "formik";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import { client } from "../../../App";
import UserContext from "../../../contexts/UserContext";
import { GenericErrorEnum } from "../../../helpers/enums";
import { TranslatedText, getValidationRule } from "../../../helpers/helpers";
import {
  NOTIFY_UPDATE_PASSWORD,
  OPERATOR_RESET_PASSWORD,
  getEvictionForMutation,
} from "../../../helpers/mutations";
import PasswordModalInfoMatch from "../../common/PasswordModalInfoMatch/PasswordModalInfoMatch";
import "./CustomForgotPassword.scss";

class CustomForgotPassword extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      step: "request",
      feedback: null,
      errMsg: {},
      token: props.token,
      isOpen: false,
    };
  }

  componentDidMount() {
    if (this.props.token) {
      this.setState({
        step: "submit",
        username: this.props.token,
        feedback: null,
      });
    }
  }

  openModalInfo(value) {
    this.setState({ isOpen: value });
  }

  handleForgotPassword = async (values, { setErrors }) => {
    const { username } = values;

    console.log("Requesting reset for:", username);

    try {
      const output = await resetPassword({ username });

      console.log("Reset Password Output: ", output);

      const nextStep = output.nextStep.signInStep;

      if (nextStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        this.setState({
          step: "submit",
          username: username,
          feedback: null,
        });
      } else {
        console.warn("Unexpected next step:", nextStep);
        this.setState({ step: "submit", username, feedback: null });
      }
    } catch (err) {
      console.log("Reset Password Error:", err);
      const { t } = this.props;

      const errorCode = err.name || err.code;

      switch (errorCode) {
        case GenericErrorEnum.LimitExceededException:
          setErrors({
            username: `${t("confirm.request.recovery.email.try")}`,
          });
          break;
        case GenericErrorEnum.UserLambdaValidationException:
          setErrors({ username: t("common.serverError") });
          break;
        case GenericErrorEnum.NotAuthorizedException:
          setErrors({ feedback: t(`error.login.${errorCode}`) });
          break;
        default:
          setErrors({ username: err.message || JSON.stringify(err) });
          break;
      }
    }
  };

  handleForgotPasswordSubmit = async (
    values,
    { resetForm, setErrors, setStatus }
  ) => {
    let formData = values;
    const { username, token } = this.state;
    const { t } = this.props;

    if (token) {
      try {
        const { password } = formData;
        console.log("Operator Reset:", password, token);

        await client().mutate({
          mutation: OPERATOR_RESET_PASSWORD,
          variables: { req: { token: token, password: password } },
        });

        this.setState({ step: "done", feedback: null });

        await client().mutate({
          mutation: NOTIFY_UPDATE_PASSWORD,
          update: (cache: any) => {
            const fieldsToEvict = getEvictionForMutation("notifyUpdatePassword");
            fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
            cache.gc();
          },
        });
      } catch (err) {
        console.error("Operator Reset Error:", err);
        if (err.code || err.message) {
          setErrors({
            password: `${t("confirm.request.recovery.code.error")}`,
          });
        }
      }
    } else {
      try {
        const { code, password } = formData;

        await confirmResetPassword({
          username: username,
          confirmationCode: code,
          newPassword: password,
        });

        console.log("Password changed successfully via Amplify.");
        this.setState({ step: "done", feedback: null });

        await signIn({ username, password });

        await client().mutate({
          mutation: NOTIFY_UPDATE_PASSWORD,
          update: (cache: any) => {
            const fieldsToEvict = getEvictionForMutation("notifyUpdatePassword");
            fieldsToEvict.forEach((field) => cache.evict({ fieldName: field }));
            cache.gc();
          },
        });

        await signOut();
      } catch (err) {
        console.error("Amplify Reset Error:", err);

        const errorCode = err.name || err.code;

        switch (errorCode) {
          case GenericErrorEnum.LimitExceededException:
            setErrors({
              username: `${t("confirm.request.recovery.email.try")}`,
            });
            break;

          case GenericErrorEnum.CodeMismatchException:
            setErrors({
              code: `${t("confirm.request.recovery.code.invalid")}`,
            });
            break;

          default:
            setErrors({
              code: `${t("confirm.request.recovery.code.error")}`,
            });
            break;
        }
      }
    }
  };

  render() {
    const { step, token, isOpen } = this.state;
    const { t } = this.props;
    const validPasswordMinNumber = getValidationRule(
      "passwordMinimumNumberOfChar"
    );
    const validPaswordUppercase = getValidationRule("passwordUppercaseChar");
    const validPaswordLowercase = getValidationRule("passwordLowercaseChar");
    const validPaswordNumeric = getValidationRule("passwordNumericChar");
    const validPaswordSpecial = getValidationRule("passwordSpecialChar");

    let schemaValidationRequest = Yup.object().shape({
      username: Yup.string().required(
        <TranslatedText i18nKey="confirm.username.required" />
      ),
    });
    let schemaValidationSubmit = Yup.object().shape({
      password: Yup.string()
        .matches(validPasswordMinNumber, {
          message: (
            <TranslatedText i18nKey="errors.change.password.validation.match" />
          ),
        })
        .matches(validPaswordUppercase, {
          message: (
            <TranslatedText i18nKey="errors.change.password.validation.match" />
          ),
        })
        .matches(validPaswordLowercase, {
          message: (
            <TranslatedText i18nKey="errors.change.password.validation.match" />
          ),
        })
        .matches(validPasswordMinNumber, {
          message: (
            <TranslatedText i18nKey="errors.change.password.validation.match" />
          ),
        })
        .matches(validPaswordNumeric, {
          message: (
            <TranslatedText i18nKey="errors.change.password.validation.match" />
          ),
        })
        .matches(validPaswordSpecial, {
          message: (
            <TranslatedText i18nKey="errors.registration.password.match" />
          ),
        })
        .required(
          <TranslatedText i18nKey="errors.registration.password.required" />
        ),
      confirmPassword: Yup.string()
        .required(
          <TranslatedText i18nKey="errors.registration.password.required" />
        )
        .oneOf(
          [Yup.ref("password"), null],
          <TranslatedText i18nKey="errors.registration.confirmPassword.combine" />
        ),
    });
    let schemaValidationCodeSubmit = Yup.object().shape({
      code: Yup.string().required(
        <TranslatedText i18nKey="request.recovery.code.required" />
      ),
    });
    const validationSubmit = token
      ? schemaValidationSubmit
      : schemaValidationSubmit.concat(schemaValidationCodeSubmit);

    return (
      <div
        className={"CustomForgotPassword mt-5"}
        data-testid="CustomForgotPassword"
      >
        {step === "request" ? (
          <Formik
            initialValues={{
              username: "",
            }}
            key={"RequestForm"}
            validationSchema={schemaValidationRequest}
            onSubmit={(value, props) => {
              return this.handleForgotPassword(value, props);
            }}
          >
            {({ errors, touched, status }) => (
              <Form noValidate autoComplete="off" role="presentation">
                <div className="row">
                  <div className="col-xs-12 col-md-10 offset-md-1">
                    <div className={"row"}>
                      <div className="col-md-6 offset-md-3 col-sm-12">
                        <p
                          className={
                            "case-step-mandatory-fields-legend paragraph-small-fs9"
                          }
                        >
                          {t("mandatory.fields.legend")}
                        </p>
                        <div className="form-group">
                          <label
                            htmlFor="username"
                            className="paragraph-small-fs9"
                          >
                            {t("confirm.email")} *
                          </label>
                          <Field
                            name={"username"}
                            id="username"
                            type="text"
                            className={
                              errors.username && touched.username
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder={t(
                              "forgotPassword.username.placeholder"
                            )}
                            required={true}
                            aria-describedby={
                              errors.username && touched.username
                                ? "username-error"
                                : null
                            }
                          />
                          {errors.username && touched.username ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"username-error"}
                            >
                              {errors.username}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className={"row footer-form-actions"}>
                      <div className={"col text-center"}>
                        <button type="submit" className={"btn btn-primary"}>
                          {t("confirm.send.email")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        ) : step === "submit" ? (
          <Formik
            initialValues={{
              code: "",
              password: "",
              confirmPassword: "",
            }}
            key={"SubmitForm"}
            validationSchema={validationSubmit}
            onSubmit={(value, props) =>
              this.handleForgotPasswordSubmit(value, props)
            }
          >
            {({ errors, touched }) => (
              <Form noValidate>
                <div className="row">
                  <div className="col-xs-12 col-md-6 offset-md-3">
                    <p
                      className={
                        "case-step-mandatory-fields-legend paragraph-small-fs9"
                      }
                    >
                      {t("mandatory.fields.legend")}
                    </p>
                    {!token ? (
                      <div className={"row"}>
                        <div className="col-lg-6 col-md-12">
                          <div className="form-group">
                            <label
                              htmlFor="code"
                              className="paragraph-small-fs9"
                            >
                              {t("confirm.code")} *
                            </label>
                            <Field
                              name="code"
                              id="code"
                              type="text"
                              className={
                                errors.code && touched.code
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              placeholder={t(
                                "forgotPassword.password.otp_code.placeholder"
                              )}
                              aria-describedby={
                                errors.code && touched.code
                                  ? "code-error"
                                  : null
                              }
                              required={true}
                            />
                            {errors.code && touched.code ? (
                              <p
                                className="form-error-text paragraph-small-form"
                                role="alert"
                                id={"code-error"}
                              >
                                {errors.code}
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-lg-6 col-md-12" />
                      </div>
                    ) : null}
                    <div className="row">
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label
                            htmlFor="password"
                            className="paragraph-small-fs9"
                          >
                            {t("confirm.new.password")} *
                            <button
                              type="button"
                              className={"form-info-button"}
                              aria-label={t("aria.form.info.button")}
                              aria-haspopup="dialog"
                              aria-controls={"password-info-modal"}
                              onClick={() => {
                                this.openModalInfo(true);
                              }}
                            >
                              <span
                                className="form-icon-info"
                                aria-hidden="true"
                              ></span>
                            </button>
                          </label>
                          <Field
                            name="password"
                            id="password"
                            type="password"
                            className={
                              errors.password && touched.password
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder={t(
                              "change.password.newPassword.placeholder"
                            )}
                            aria-describedby={
                              errors.password && touched.password
                                ? "password-error"
                                : null
                            }
                            required={true}
                          />
                          {errors.password && touched.password ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"password-error"}
                            >
                              {errors.password}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                          <label
                            htmlFor="confirmPassword"
                            className="paragraph-small-fs9"
                          >
                            {t("confirm.new.password.confirm")} *
                          </label>
                          <Field
                            name="confirmPassword"
                            id="confirmPassword"
                            type="password"
                            className={
                              errors.confirmPassword && touched.confirmPassword
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            placeholder={t(
                              "change.password.newPassword.placeholder"
                            )}
                            aria-describedby={
                              errors.confirmPassword && touched.confirmPassword
                                ? "confirmPassword-error"
                                : null
                            }
                            required={true}
                          />
                          {errors.confirmPassword && touched.confirmPassword ? (
                            <p
                              className="form-error-text paragraph-small-form"
                              role="alert"
                              id={"confirmPassword-error"}
                            >
                              {errors.confirmPassword}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className={"row footer-form-actions"}>
                      <div className="col text-center">
                        <button type="submit" className={"btn btn-primary"}>
                          {t("confirm.save")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div className="row">
            <div className="col-xs-12 col-md-6 offset-md-3">
              <div className={"row"}>
                <div className="col-lg-12 col-md-12 text-center">
                  <h3>{t("confirm.confirmed")}</h3>
                </div>
              </div>
            </div>
          </div>
        )}
        <PasswordModalInfoMatch
          show={isOpen}
          onClose={() => this.openModalInfo(false)}
        />
      </div>
    );
  }
}

export default withTranslation("translation")(CustomForgotPassword);
